/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { AppPathsMap } from '@core/routing/app-paths';
import { ResponseWorkItemCacheDto } from '@kerberos-compliance/compliance-shared-ts/features/work-item/dtos/response.work-item.dtos';
import { WorkItemStatusMap } from '@kerberos-compliance/compliance-shared-ts/features/work-item/enums/work-item-status.enum';
import {
  AbstractNavigationService,
  NavigationOptions,
  NavigationResult,
} from '@kerberos-compliance/compliance-shared-ui';

/**
 * NavigationService – The ONLY class allowed injecting Angular Router in aml-desk.
 *
 * All programmatic navigation MUST go through this service.
 * Components should NEVER inject Router directly.
 *
 * Generic navigation utilities (navigateTo, navigateByUrl, goBack, etc.)
 * are inherited from AbstractNavigationService. Prefer the generic
 * `navigateTo(key, params?, extras?)` for new call sites; the intent-named
 * `goTo*` methods below are thin deprecated wrappers kept for compatibility.
 */
@Injectable({ providedIn: 'root' })
export class NavigationService extends AbstractNavigationService {
  // ===========================================
  // DASHBOARD & HOME NAVIGATION
  // ===========================================

  /**
   * Navigates the user to the dashboard page of the application.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to `true` if the navigation was successful, otherwise `false`.
   * @deprecated Use `navigateTo('dashboard')`.
   */
  public async goToDashboard(): Promise<NavigationResult> {
    return this.navigateTo('dashboard');
  }

  /**
   * Navigates the application to the home page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('home')`.
   */
  public async goToHome(): Promise<NavigationResult> {
    return this.navigateTo('home');
  }

  /**
   * Performs a FULL-PAGE load instead of an in-app router navigation. Used right
   * after a Hanko login so the browser tears down any pending WebAuthn
   * conditional-mediation (passkey autofill) request started on the login screen:
   * the Hanko SDK exposes no way to abort it, and an in-app (SPA) navigation would
   * leave it dangling, which Chrome later surfaces as an unexpected native passkey
   * dialog. The freshly issued tokens are already persisted to storage and
   * re-hydrated on load, so the session survives the reload (KCP-1160).
   *
   * @param {string} [path] - App-internal absolute path to load; defaults to the home
   * route. Callers must pass a value already validated as internal (see
   * `sanitizeReturnUrl`) — this performs a raw browser navigation.
   * @returns {void} This method does not return a value.
   */
  public goToWithReload(path?: string): void {
    globalThis.location.assign(path ?? `/${AppPathsMap.Home}`);
  }

  /**
   * Navigates the application to the homepage.
   *
   * Note: the homepage (`/`) is not a registry route, so this keeps navigating by path segment.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to true if the navigation was successful, otherwise false.
   */
  public async goToHomepage(): Promise<NavigationResult> {
    return this.navigateTo([AppPathsMap.Homepage]);
  }

  /**
   * Navigates the user to the logout page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('logout')`.
   */
  public async goToLogout(): Promise<NavigationResult> {
    return this.navigateTo('logout');
  }

  /**
   * Navigates the user to the login page — the application's single front door.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('login')`.
   */
  public async goToLogin(): Promise<NavigationResult> {
    return this.navigateTo('login');
  }

  // ===========================================
  // KYC NAVIGATION (Intent-Based Methods)
  // ===========================================

  /**
   * Navigates the user to the KYC (Know Your Customer) overview page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('kyc.overview')`.
   */
  public async goToKycOverview(): Promise<NavigationResult> {
    return this.navigateTo('kyc.overview');
  }

  /**
   * Navigates the user to the KYC (Know Your Customer) dashboard within the application.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation to the KYC dashboard was successful.
   * @deprecated Use `navigateTo('kyc.dashboard')`.
   */
  public async goToKycDashboard(): Promise<NavigationResult> {
    return this.navigateTo('kyc.dashboard');
  }

  /**
   * Navigates the user to the KYC (Know Your Customer) creation screen within the application.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('kyc.create')`.
   */
  public async goToKycCreate(): Promise<NavigationResult> {
    return this.navigateTo('kyc.create');
  }

  /**
   * Navigates to the KYC edit page for the specified work item.
   *
   * @param {string} workItemId - The unique identifier of the work item to edit.
   * @param {NavigationOptions} [options] - Optional navigation options for customizing the navigation behavior.
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('kyc.workItemCreate', { workItemId })`.
   */
  public async goToKycEdit(workItemId: string, options?: NavigationOptions): Promise<NavigationResult> {
    if (!workItemId) {
      this.logError('goToKycEdit', 'Missing workItemId parameter');
      return { success: false };
    }
    return this.navigateTo('kyc.workItemCreate', { workItemId }, options);
  }

  /**
   * Navigates to the KYC review page for the given work item.
   *
   * @param {string} workItemId - The unique identifier of the work item to review.
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('kyc.workItemReview', { workItemId })`.
   */
  public async goToKycReview(workItemId: string): Promise<NavigationResult> {
    if (!workItemId) {
      this.logError('goToKycReview', 'Missing workItemId parameter');
      return { success: false };
    }
    return this.navigateTo('kyc.workItemReview', { workItemId });
  }

  /**
   * Navigate the user to kyc edit or review route based on the work item status
   *
   * @param {ResponseWorkItemCacheDto} workItem - details of the work item
   * @param {NavigationOptions}[options] - Optional navigation options for customizing the navigation behavior.
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   */
  public async goToKycWorkItem(
    workItem: ResponseWorkItemCacheDto,
    options?: NavigationOptions,
  ): Promise<NavigationResult> {
    if (!workItem.id) {
      this.logError('goToKycWorkItem', 'Missing workItemId parameter');
      return { success: false };
    }
    const workItemId = workItem.id;
    return workItem.status === WorkItemStatusMap.Draft
      ? this.navigateTo('kyc.workItemCreate', { workItemId }, options)
      : this.navigateTo('kyc.workItemReview', { workItemId });
  }

  // ===========================================
  // RISK ANALYSIS NAVIGATION
  // ===========================================

  /**
   * Navigates the user to the Risk Analysis Introduction page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to true if the navigation is successful, otherwise false.
   * @deprecated Use `navigateTo('riskAnalysis.introduction')`.
   */
  public async goToRiskAnalysisIntroduction(): Promise<NavigationResult> {
    return this.navigateTo('riskAnalysis.introduction');
  }

  /**
   * Navigates the application to the risk questionnaire screen.
   *
   * @param {NavigationOptions} [options] - Optional navigation parameters.
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('riskAnalysis.questionnaire', undefined, extras)`.
   */
  public async goToRiskQuestionnaire(options?: NavigationOptions): Promise<NavigationResult> {
    return this.navigateTo('riskAnalysis.questionnaire', undefined, options);
  }

  /**
   * Navigates to the "Risk Analysis Complete" page and initializes the navigation state with the necessary feedback parameter.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('riskAnalysis.introduction', undefined, { extras: { state: { showFeedback: true } } })`.
   */
  public async goToRiskAnalysisComplete(): Promise<NavigationResult> {
    const options = { extras: { state: { showFeedback: true } } };
    return this.navigateTo('riskAnalysis.introduction', undefined, options);
  }

  // ===========================================
  // LOCATION ANALYSIS NAVIGATION
  // ===========================================

  /**
   * Navigates the application to the introduction section of the location analysis module.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('locationAnalysis.introduction')`.
   */
  public async goToLocationAnalysisIntroduction(): Promise<NavigationResult> {
    return this.navigateTo('locationAnalysis.introduction');
  }

  /**
   * Navigates to the "Location Analysis Create" page within the application.
   *
   * @param {boolean} [viaProcess] Indicates whether the navigation is triggered via a specific process or not.
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating the success of the navigation operation.
   * @deprecated Use `navigateTo('locationAnalysis.create', undefined, { extras: { state: { redirectViaProcess } } })`.
   */
  public async goToLocationAnalysisCreate(viaProcess: boolean = false): Promise<NavigationResult> {
    const options = { extras: { state: { redirectViaProcess: viaProcess } } };
    return this.navigateTo('locationAnalysis.create', undefined, options);
  }

  // ===========================================
  // ERROR PAGE NAVIGATION
  // ===========================================

  /**
   * Navigates the user to the unauthorized error page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating the navigation status.
   * @deprecated Use `navigateTo('unauthorized')`.
   */
  public async goToUnauthorized(): Promise<NavigationResult> {
    return this.navigateTo('unauthorized');
  }

  /**
   * Navigates the application to the "Not Found" error page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('notFound')`.
   */
  public async goToNotFound(): Promise<NavigationResult> {
    return this.navigateTo('notFound');
  }

  /**
   * Navigates the application to the "No Grants" error page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether navigation was successful.
   * @deprecated Use `navigateTo('noGrants')`.
   */
  public async goToNoGrants(): Promise<NavigationResult> {
    return this.navigateTo('noGrants');
  }

  /**
   * Navigates the application to the maintenance screen.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('maintenance')`.
   */
  public async goToMaintenance(): Promise<NavigationResult> {
    return this.navigateTo('maintenance');
  }

  /**
   * Navigates the application to the "Backend Unavailable" error page.
   *
   * @returns {Promise<NavigationResult>} A promise resolving to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('backendUnavailable')`.
   */
  public async goToBackendUnavailable(): Promise<NavigationResult> {
    return this.navigateTo('backendUnavailable');
  }

  /**
   * Navigates to the Grant Load Error page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('grantLoadError')`.
   */
  public async goToGrantLoadError(): Promise<NavigationResult> {
    return this.navigateTo('grantLoadError');
  }

  /**
   * Navigates to the Login Error page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('loginError')`.
   */
  public async goToLoginError(): Promise<NavigationResult> {
    return this.navigateTo('loginError');
  }

  /**
   * Returns the absolute URL for the login error page.
   * Used for hard-redirect fallbacks when Angular navigation is unavailable.
   *
   * @returns {string} The full login error page URL.
   */
  public getLoginErrorUrl(): string {
    const path = `/${AppPathsMap.Error.LoginError}`;
    const origin = globalThis.location?.origin;
    return origin ? `${origin}${path}` : path;
  }

  /**
   * Navigates to the "User Not Active" error page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating
   * whether the navigation was successful.
   * @deprecated Use `navigateTo('userNotActive')`.
   */
  public async goToUserNotActive(): Promise<NavigationResult> {
    return this.navigateTo('userNotActive');
  }

  /**
   * Navigates to the "Session Unavailable" error page, shown when an in-session
   * token refresh fails with 401.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating
   * whether the navigation was successful.
   * @deprecated Use `navigateTo('sessionUnavailable')`.
   */
  public async goToSessionUnavailable(): Promise<NavigationResult> {
    return this.navigateTo('sessionUnavailable');
  }

  // ===========================================
  // UTILITY METHODS
  // ===========================================

  /**
   * Navigates the application to the contact page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation to the contact page was successful.
   * @deprecated Use `navigateTo('contact')`.
   */
  public async goToContact(): Promise<NavigationResult> {
    return this.navigateTo('contact');
  }

  /**
   * Navigates the user to the FAQ page of the application.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to true if navigation is successful, otherwise false.
   * @deprecated Use `navigateTo('faq')`.
   */
  public async goToFaq(): Promise<NavigationResult> {
    return this.navigateTo('faq');
  }

  /**
   * Navigates to the Transparency Register page within the application.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to true if the navigation is successful, otherwise false.
   * @deprecated Use `navigateTo('transparencyRegister')`.
   */
  public async goToTransparencyRegister(): Promise<NavigationResult> {
    return this.navigateTo('transparencyRegister');
  }

  /**
   * Navigates the user to the document library.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('documentLibrary')`.
   */
  public async goToDocumentLibrary(): Promise<NavigationResult> {
    return this.navigateTo('documentLibrary');
  }

  /**
   * Navigates the user to the training section of the application.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to true if the navigation is successful, otherwise false.
   * @deprecated Use `navigateTo('training')`.
   */
  public async goToTraining(): Promise<NavigationResult> {
    return this.navigateTo('training');
  }

  /**
   * Navigates the application to the Contract Expired page.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   * @deprecated Use `navigateTo('contractExpired')`.
   */
  public async goToContractExpired(): Promise<NavigationResult> {
    return this.navigateTo('contractExpired');
  }

  /**
   * Navigates to the "Icon Example" page within the application.
   *
   * Note: not a registry route, so this keeps navigating by path segment.
   *
   * @returns {Promise<NavigationResult>} A promise that resolves to a boolean indicating whether the navigation was successful.
   */
  public async goToIconExample(): Promise<NavigationResult> {
    return this.navigateTo([AppPathsMap.IconExample]);
  }

  /**
   * Navigates the user back to the previous page if a valid navigation history exists.
   * If no such history exists, redirects to a specified fallback path.
   *
   * @param {string} fallbackPath - The default route to navigate to if no valid navigation history exists. Defaults to AppPathsMap.Dashboard.
   * @returns {Promise<NavigationResult>} Promise resolving to a NavigationResult.
   */
  public override async goBack(fallbackPath: string = AppPathsMap.Dashboard): Promise<NavigationResult> {
    return super.goBack(fallbackPath);
  }
}
